import * as yup from "yup";
import {
  CONDITIONS,
  GENDER,
  MARTIAL_STATUSES,
  POPULATION_FOCUSES,
  RACES,
} from "./common";
import { format, parseISO } from "date-fns";
export const US_PHONE_REGEX = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/; // US phone_number pattern
const US_ZIP_CODE_REGEX = /^[0-9]{5}(-[0-9]{4})?$/; // US ZIP code (5 digits)
export const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];
export const formatPhoneNumber = (value, previousValue = 0) => {
  if (!value) return value;
  const currentValue = value.replace(/[^\d]/g, "");
  const cvLength = currentValue.length;

  if (!previousValue || value.length > previousValue.length) {
    if (cvLength < 4) return currentValue;
    if (cvLength < 7)
      return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
    return `(${currentValue.slice(0, 3)}) ${currentValue.slice(
      3,
      6,
    )}-${currentValue.slice(6, 10)}`;
  }
};
export const isValidPhoneNumber = (phoneNumber) =>
  US_PHONE_REGEX.test(phoneNumber);

export const memberSchema = yup.object().shape({
  // Personal Info
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  date_of_birth: yup.date().required("Date of Birth is required"),
  gender: yup.string(),

  // Membership Info
  cin: yup.string().required("CIN is required"),
  aica_account: yup.string(),
  kipu_account_no: yup.string().required("KIPU Account No is required"),
  health_plan_id: yup.mixed().required("Health Plan is required"),
  // kipu_ifmrn: yup.string().required("KIPU IFMRN is required"),
  housing_navigator: yup.string().required("Housing Navigator is required"),
  case_manager_id: yup.mixed().required("CSS Manager is required"),

  // Contact Info
  phone_number: yup
    .string()
    .matches(US_PHONE_REGEX, "Phone number is not valid *"),
  email: yup.string().email(),
  home_address: yup.string(),
  city_id: yup.mixed(),
  state_id: yup.mixed().required("Home State is required"),
  county_id: yup.mixed().required("Home County is required"),
  zip_code_id: yup.mixed(),

  mailing_address: yup.string(),
  mailing_zip_code_id: yup.mixed(),
  mailing_city_id: yup.mixed(),
  mailing_state_id: yup.mixed(),
  mailing_county_id: yup.mixed(),

  // Emergency Contact
  emergency_contact_name: yup.string(),
  emergency_contact_relationship: yup.string(),

  // Communication Preferences
  preferred_spoken_language: yup
    .string()
    .required("Preferred Spoken Language is required"),
  preferred_written_language: yup.string(),

  // Property Information
  housing_database: yup.mixed(),
  housing_unit: yup.mixed(),
  landlord_name: yup.mixed(),

  // Program Enrollments - Status fields
  calaim_css_housing_ie_status: yup.string(),
  calaim_css_housing_la_status: yup.string(),
  calaim_css_housing_oc_status: yup.string(),
  calaim_css_recup_ie_status: yup.string(),
  calaim_css_recup_la_status: yup.string(),
  calaim_css_recup_oc_status: yup.string(),
  calaim_css_short_term_ie_status: yup.string(),
  calaim_css_short_term_la_status: yup.string(),
  calaim_css_short_term_oc_status: yup.string(),
  calaim_css_tenancy_ie_status: yup.string(),
  calaim_css_tenancy_la_status: yup.string(),
  calaim_css_tenancy_oc_status: yup.string(),

  // Program Enrollments - IFMRN fields (required when status is active)
  calaim_css_housing_ie_ifmrn: yup
    .string()
    .when("calaim_css_housing_ie_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_housing_la_ifmrn: yup
    .string()
    .when("calaim_css_housing_la_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_housing_oc_ifmrn: yup
    .string()
    .when("calaim_css_housing_oc_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_recup_ie_ifmrn: yup.string().when("calaim_css_recup_ie_status", {
    is: "active",
    then: yup
      .string()
      .required("KIPU IF MRN is required when status is active"),
    otherwise: yup.string(),
  }),
  calaim_css_recup_la_ifmrn: yup.string().when("calaim_css_recup_la_status", {
    is: "active",
    then: yup
      .string()
      .required("KIPU IF MRN is required when status is active"),
    otherwise: yup.string(),
  }),
  calaim_css_recup_oc_ifmrn: yup.string().when("calaim_css_recup_oc_status", {
    is: "active",
    then: yup
      .string()
      .required("KIPU IF MRN is required when status is active"),
    otherwise: yup.string(),
  }),
  calaim_css_short_term_ie_ifmrn: yup
    .string()
    .when("calaim_css_short_term_ie_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_short_term_la_ifmrn: yup
    .string()
    .when("calaim_css_short_term_la_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_short_term_oc_ifmrn: yup
    .string()
    .when("calaim_css_short_term_oc_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_tenancy_ie_ifmrn: yup
    .string()
    .when("calaim_css_tenancy_ie_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_tenancy_la_ifmrn: yup
    .string()
    .when("calaim_css_tenancy_la_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),
  calaim_css_tenancy_oc_ifmrn: yup
    .string()
    .when("calaim_css_tenancy_oc_status", {
      is: "active",
      then: yup
        .string()
        .required("KIPU IF MRN is required when status is active"),
      otherwise: yup.string(),
    }),

  // Other
  population_focus: yup.array(),

  conditions: yup.array(),
});

export const memberInitialValues = (selectedMember) => {
  console.log(
    "THIS IS INSITE THE INTIAL VALUES SELECTED MEMBER",
    selectedMember,
  );
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date =
        typeof dateStr === "string" ? parseISO(dateStr) : new Date(dateStr);
      return !isNaN(date.getTime()) ? format(date, "MM/dd/yyyy") : "";
    } catch {
      return "";
    }
  };

  if (selectedMember) {
    const homeAddress = selectedMember?.addresses?.find(
      (addr) => addr.address_type === "home",
    );
    const mailingAddress = selectedMember?.addresses?.find(
      (addr) => addr.address_type === "mailing",
    );

    const initialValues = {
      name: selectedMember?.name || "",
      first_name: selectedMember?.first_name || "",
      last_name: selectedMember?.last_name || "",
      date_of_birth: formatDate(selectedMember?.date_of_birth),
      gender: selectedMember?.gender,

      race: selectedMember?.race || "",
      marital: selectedMember?.marital || "",

      // Membership Info
      cin: selectedMember?.cin || "",
      aica_account: selectedMember?.aica_account || "",
      kipu_account_no: selectedMember?.kipu_account_no || "",
      kipu_ifmrn: selectedMember?.kipu_ifmrn || "",
      health_plan_id: {
        value: selectedMember?.health_plan?.id,
        label: selectedMember?.health_plan?.name,
      },
      if_number: selectedMember?.if_number || "",
      housing_navigator: selectedMember?.housing_navigator || "",
      case_manager_id: {
        value: selectedMember?.case_manager_id,
        label: selectedMember?.cs_manager,
      },

      // Contact Info
      home_address: selectedMember?.home_street || "",
      zip_code_id: {
        value: selectedMember?.home_zip?.id,
        label: selectedMember?.home_zip?.name,
      },
      phone_number: selectedMember?.phone_number || "",
      email: selectedMember?.email || "",
      mailing_address: selectedMember?.mailing_street || "",
      mailing_zip_code_id: {
        value: selectedMember?.mailing_zip?.id,
        label: selectedMember?.mailing_zip?.name,
      },

      state_id: {
        value: selectedMember?.home_state?.id || "",
        label: selectedMember?.home_state?.name || "",
      },
      city_id: {
        value: selectedMember?.home_city?.id || "",
        label: selectedMember?.home_city?.name || "",
      },
      county_id: {
        value: selectedMember?.home_county?.id || "",
        label: selectedMember?.home_county?.name || "",
      },
      mailing_state_id: {
        value: selectedMember?.mailing_state?.id || "",
        label: selectedMember?.mailing_state?.name || "",
      },
      mailing_city_id: {
        value: selectedMember?.mailing_city?.id || "",
        label: selectedMember?.mailing_city?.name || "",
      },
      mailing_county_id: {
        value: selectedMember?.mailing_county?.id || "",
        label: selectedMember?.mailing_county?.name || "",
      },

      // Emergency Contact
      emergency_contact_name: selectedMember?.emergency_contact_name || "",
      emergency_contact_relationship:
        selectedMember?.emergency_contact_relationship || "",

      // Communication Preferences
      preferred_spoken_language:
        selectedMember?.preferred_spoken_language || "",
      preferred_written_language:
        selectedMember?.preferred_written_language || "",

      // Property Information
      housing_database: selectedMember?.housing_database?.property_name
        ? {
            label: selectedMember?.housing_database?.property_name,
            value: selectedMember?.housing_database?.id,
          }
        : {} || "",
      housing_unit: selectedMember?.housing_unit
        ? {
            value: selectedMember.housing_unit,
            label: selectedMember.housing_unit,
          }
        : null,

      landlord_name: selectedMember?.landlord_name
        ? {
            value: selectedMember.landlord?.id || "",
            label:
              selectedMember.landlord_name ||
              selectedMember.landlord?.name ||
              "",
          }
        : null,
      // Program Enrollments - Status fields
      calaim_css_housing_ie_status:
        selectedMember?.calaim_css_housing_ie_status || "",
      calaim_css_housing_la_status:
        selectedMember?.calaim_css_housing_la_status || "",
      calaim_css_housing_oc_status:
        selectedMember?.calaim_css_housing_oc_status || "",
      calaim_css_recup_ie_status:
        selectedMember?.calaim_css_recup_ie_status || "",
      calaim_css_recup_la_status:
        selectedMember?.calaim_css_recup_la_status || "",
      calaim_css_recup_oc_status:
        selectedMember?.calaim_css_recup_oc_status || "",
      calaim_css_short_term_ie_status:
        selectedMember?.calaim_css_short_term_ie_status || "",
      calaim_css_short_term_la_status:
        selectedMember?.calaim_css_short_term_la_status || "",
      calaim_css_short_term_oc_status:
        selectedMember?.calaim_css_short_term_oc_status || "",
      calaim_css_tenancy_ie_status:
        selectedMember?.calaim_css_tenancy_ie_status || "",
      calaim_css_tenancy_la_status:
        selectedMember?.calaim_css_tenancy_la_status || "",
      calaim_css_tenancy_oc_status:
        selectedMember?.calaim_css_tenancy_oc_status || "",

      // Program Enrollments - IFMRN fields (required when status is active)
      calaim_css_housing_ie_ifmrn:
        selectedMember?.calaim_css_housing_ie_ifmrn || "",
      calaim_css_housing_la_ifmrn:
        selectedMember?.calaim_css_housing_la_ifmrn || "",
      calaim_css_housing_oc_ifmrn:
        selectedMember?.calaim_css_housing_oc_ifmrn || "",
      calaim_css_recup_ie_ifmrn:
        selectedMember?.calaim_css_recup_ie_ifmrn || "",
      calaim_css_recup_la_ifmrn:
        selectedMember?.calaim_css_recup_la_ifmrn || "",
      calaim_css_recup_oc_ifmrn:
        selectedMember?.calaim_css_recup_oc_ifmrn || "",
      calaim_css_short_term_ie_ifmrn:
        selectedMember?.calaim_css_short_term_ie_ifmrn || "",
      calaim_css_short_term_la_ifmrn:
        selectedMember?.calaim_css_short_term_la_ifmrn || "",
      calaim_css_short_term_oc_ifmrn:
        selectedMember?.calaim_css_short_term_oc_ifmrn || "",
      calaim_css_tenancy_ie_ifmrn:
        selectedMember?.calaim_css_tenancy_ie_ifmrn || "",
      calaim_css_tenancy_la_ifmrn:
        selectedMember?.calaim_css_tenancy_la_ifmrn || "",
      calaim_css_tenancy_oc_ifmrn:
        selectedMember?.calaim_css_tenancy_oc_ifmrn || "",

      // Other
      population_focus:
        POPULATION_FOCUSES.filter((c) =>
          selectedMember?.population_focus
            ?.split(",")
            .map((item) => item.trim())
            .includes(c.value),
        ) || [],
      conditions:
        CONDITIONS.filter((c) =>
          selectedMember?.conditions
            ?.split(",")
            .map((s) => s.trim())
            .includes(c.value),
        ) || [],
    };
    return initialValues;
  }

  return {
    // Personal Info
    name: "",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    race: "",
    marital: "",

    // Membership Info
    cin: "",
    aica_account: "",
    kipu_account_no: "",
    health_plan_id: "",
    if_number: "",
    housing_navigator: "",
    case_manager_id: "",

    // Contact Info
    phone_number: "",
    email: "",
    home_address: "",
    home_zip_code: "",
    mailing_address: "",
    mailing_zip_code_id: "",
    state_id: "",
    city_id: "",
    // county_id: "",
    mailing_state_id: "",
    mailing_city_id: "",
    mailing_county_id: "",

    // Emergency Contact
    emergency_contact_name: "",
    emergency_contact_relationship: "",

    // Communication Preferences
    preferred_spoken_language: "",
    preferred_written_language: "",

    // Property Information
    housing_database: "",
    housing_unit: "",
    landlord_name: "",

    // Program Enrollments - Status fields
    calaim_css_housing_ie_status: "",
    calaim_css_housing_la_status: "",
    calaim_css_housing_oc_status: "",
    calaim_css_recup_ie_status: "",
    calaim_css_recup_la_status: "",
    calaim_css_recup_oc_status: "",
    calaim_css_short_term_ie_status: "",
    calaim_css_short_term_la_status: "",
    calaim_css_short_term_oc_status: "",
    calaim_css_tenancy_ie_status: "",
    calaim_css_tenancy_la_status: "",
    calaim_css_tenancy_oc_status: "",

    // Program Enrollments - IFMRN fields (required when status is active)
    calaim_css_housing_ie_ifmrn: "",
    calaim_css_housing_la_ifmrn: "",
    calaim_css_housing_oc_ifmrn: "",
    calaim_css_recup_ie_ifmrn: "",
    calaim_css_recup_la_ifmrn: "",
    calaim_css_recup_oc_ifmrn: "",
    calaim_css_short_term_ie_ifmrn: "",
    calaim_css_short_term_la_ifmrn: "",
    calaim_css_short_term_oc_ifmrn: "",
    calaim_css_tenancy_ie_ifmrn: "",
    calaim_css_tenancy_la_ifmrn: "",
    calaim_css_tenancy_oc_ifmrn: "",

    // Other
    population_focus: [],
    conditions: [],
    same_as: false,
  };
};
